import { apiRequest } from "./client";

/** The v2.2 "Data" area (backend/api/admin/data_hub.py): tables grouped by
 * SharePoint site, a one-step connect and safe removal. */

export type TableProblem = "no_columns" | "no_sheet" | null;

export interface CatalogTable {
  id: string;
  name: string;
  description: string | null;
  /** Sheet name, or "table:<name>" for an Excel Table (v2.3). */
  sheet: string | null;
  kind?: "table" | "sheet" | null;
  /** 1-based (row 1 = the first row). */
  header_row: number | null;
  /** Active Applied steps (clean-up) on this table. */
  step_count?: number;
  sheet_pinned: boolean;
  field_count: number;
  measure_count: number;
  is_active: boolean;
  problem: TableProblem;
  used_by: Array<{ id: string; name: string }>;
  last_refresh: { status: string; at: string; error: string | null } | null;
  created_at: string | null;
}

export interface CatalogSource {
  id: string;
  name: string;
  label: string;
  type: "sharepoint" | "rest_api" | "postgresql" | "sql_server" | "excel" | "manual_json";
  description: string | null;
  folder_path: string | null;
  locator: { tenant_id?: string; site_id?: string; site_name?: string; drive_id?: string; item_id?: string; list_id?: string };
  is_active: boolean;
  created_at: string | null;
  tables: CatalogTable[];
}

export interface AccessGrant {
  principal_type: "user" | "role";
  principal_value: string;
  label?: string;
}

export interface CatalogGroup {
  name: string;
  /** Stable key for access grants: the SharePoint site id, else the name. */
  key: string;
  site_id: string | null;
  sources: CatalogSource[];
  grants: AccessGrant[];
}

export interface Catalog {
  groups: CatalogGroup[];
  problem_count: number;
}

export interface SourceSpec {
  kind: "excel" | "sharepoint_list";
  tenant_id?: string;
  site_id?: string;
  site_name?: string;
  drive_id?: string;
  item_id?: string;
  file_name?: string;
  folder_path?: string;
  list_id?: string;
  list_name?: string;
}

export interface DetectedColumn {
  source_name: string;
  display_name: string;
  data_type: "string" | "number" | "date" | "datetime" | "boolean";
  field_role: "dimension" | "measure" | "date" | "identifier" | "geography";
  default_aggregation: string | null;
}

export interface TablePreview {
  columns: DetectedColumn[];
  raw_rows: Array<{ row_number: number; values: Array<string | null> }>;
  /** First rows after the Applied steps (v2.3). */
  rows?: Array<Record<string, unknown>>;
  header_row: number;
}

/** One Power Query-style clean-up step (backend/query_engine/transforms.py). */
export interface CleanStep {
  type: string;
  config: Record<string, unknown>;
  label?: string | null;
  is_active?: boolean;
}

export type CandidateRole = "data" | "pivot" | "layout" | "notes" | "has_tables" | "empty";

/** v2.3 Navigator: one usable object in a file (backend/services/workbook_profiler.py). */
export interface Candidate {
  /** What to store as the table's sheet: a sheet name or "table:<name>"; null for a list. */
  key: string | null;
  kind: "table" | "sheet" | "list";
  name: string;
  sheet: string | null;
  ref: string | null;
  role: CandidateRole;
  selected: boolean;
  reason: string | null;
  header_row: number;
  row_count: number;
  column_count: number;
  columns: string[];
  sample: Array<Record<string, unknown>>;
  steps: CleanStep[];
  existing?: { id: string; name: string } | null;
}

export interface CleanupSuggestion {
  current: { header_row: number; steps: CleanStep[] };
  suggested: (Partial<Candidate> & { steps: CleanStep[]; header_row: number }) | null;
  sheet: string | null;
}

export interface CleanupResult extends Reconciliation {
  measures_created: string[];
}

export interface SuggestedModelLink {
  fromDatasetId: string;
  fromField: string;
  toDatasetId: string;
  toField: string;
  crossFilter: "both" | "single";
  cardinality: "many_to_one" | "one_to_one" | "many_to_many";
  confidence: number;
  why: string;
}

export interface ConnectResult {
  source_id: string;
  source_name: string;
  source_created: boolean;
  tables: Array<{ id: string; name: string; sheet: string | null; status: "created" | "existing"; field_count: number; measure_count?: number; step_count?: number; error: string | null }>;
}

export interface ConnectTable {
  sheet?: string | null;
  name?: string;
  /** 1-based. */
  header_row?: number | null;
  steps?: CleanStep[];
}

export interface ExcelSheet {
  name: string;
  visibility?: string;
}

export interface Reconciliation {
  added: string[];
  kept: string[];
  removed: string[];
  blocked: Array<{ source_name: string; dependencies: Array<{ kind: string; name: string }> }>;
}

export const dataHubApi = {
  catalog: () => apiRequest<Catalog>("/api/v1/admin/data/catalog"),
  preview: (source: SourceSpec, sheet?: string | null, headerRow?: number | null, steps?: CleanStep[]) =>
    apiRequest<TablePreview>("/api/v1/admin/data/preview", { method: "POST", body: { source, sheet: sheet ?? null, header_row: headerRow ?? null, steps: steps ?? [] } }),
  /** v2.3 Navigator: every sheet / Excel Table in the file, what's real data, and its clean-up. */
  profile: (source: SourceSpec) =>
    apiRequest<{ candidates: Candidate[]; source_id: string | null }>("/api/v1/admin/data/profile", { method: "POST", body: { source } }),
  connect: (source: SourceSpec, tables: ConnectTable[], autoMeasures = true) =>
    apiRequest<ConnectResult>("/api/v1/admin/data/connect", { method: "POST", body: { source, tables, auto_measures: autoMeasures } }),
  cleanup: (datasetId: string) => apiRequest<CleanupSuggestion>(`/api/v1/admin/data/tables/${datasetId}/cleanup`),
  applyCleanup: (datasetId: string, body: { header_row?: number | null; steps: CleanStep[]; auto_measures?: boolean }) =>
    apiRequest<CleanupResult>(`/api/v1/admin/data/tables/${datasetId}/cleanup`, { method: "PUT", body }),
  /** Links worth adding between a report's tables (owners and admins). */
  suggestLinks: (datasetIds: string[], linked: Array<[string, string]> = []) =>
    apiRequest<{ links: SuggestedModelLink[] }>("/api/v1/owner/datasets/links/suggest", { method: "POST", body: { dataset_ids: datasetIds, linked } }),
  deleteTable: (id: string) => apiRequest<void>(`/api/v1/admin/data/tables/${id}`, { method: "DELETE" }),
  deleteSource: (id: string) => apiRequest<void>(`/api/v1/admin/data/sources/${id}`, { method: "DELETE" }),
  listSheets: (driveId: string, itemId: string, tenantId: string) =>
    apiRequest<ExcelSheet[]>(
      `/api/v1/admin/sharepoint/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/sheets?tenant_id=${encodeURIComponent(tenantId)}`,
    ),
  detectColumns: (datasetId: string) =>
    apiRequest<Reconciliation>(`/api/v1/admin/datasets/${datasetId}/fields/reconcile`, { method: "POST" }),
  updateTable: (datasetId: string, patch: { name?: string; description?: string; source_sheet?: string; source_object?: string; header_row?: number; is_active?: boolean }) =>
    apiRequest<unknown>(`/api/v1/admin/datasets/${datasetId}`, { method: "PATCH", body: patch }),
  setAccess: (groupKey: string, groupName: string, grants: AccessGrant[]) =>
    apiRequest<{ group_key: string; grants: AccessGrant[] }>("/api/v1/admin/data/access", {
      method: "PUT",
      body: { group_key: groupKey, group_name: groupName, grants: grants.map(({ principal_type, principal_value }) => ({ principal_type, principal_value })) },
    }),
  sourceSheets: (sourceId: string) =>
    apiRequest<Array<{ type: string; name: string; row_count?: number; column_count?: number; first_columns?: string[]; hidden?: boolean }>>(
      `/api/v1/admin/data-sources/${sourceId}/objects`,
    ),
};
